import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Tenant { readonly id: string; readonly tenantCode: string; readonly displayName: string; readonly recordStatusCode: string; }

export function TenantEditPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const nav = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
      apiRequest<Tenant>(`/platform/tenants/${id}`, { token })
      .then((tn) => { if (alive) { setTenant(tn); setName(tn.displayName); setStatus(tn.recordStatusCode); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiRequest(`/platform/tenants/${id}`, { token, method: 'PUT', body: { displayName: name, recordStatusCode: status } });
      nav('/admin/tenants');
    } catch (err) { setError((err as Error).message); setSaving(false); }
  };

  if (error) return <p role="alert">{error}</p>;
  if (!tenant) return <p>{t('tenants.edit.loading')}</p>;
  return (
    <main aria-label="tenant-edit">
      <h1>{t('tenants.edit.title', { code: tenant.tenantCode })}</h1>
      <form onSubmit={onSubmit} aria-label="tenant-edit-form">
        <label>{t('tenants.edit.name')} <input value={name} onChange={(e) => setName(e.target.value)} required /></label>
        <label>{t('tenants.edit.status')}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ACTIVE">{t('tenants.edit.status.active')}</option>
            <option value="INACTIVE">{t('tenants.edit.status.inactive')}</option>
            <option value="SUSPENDED">{t('tenants.edit.status.suspended')}</option>
          </select>
        </label>
        <button type="submit" disabled={saving}>{saving ? t('tenants.edit.saving') : t('tenants.edit.save')}</button>
      </form>
    </main>
  );
}
